import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import {
  format,
  isValid,
  parse,
  startOfWeek,
  getDay,
  parseISO,
  differenceInSeconds
} from 'date-fns';
import { DateRangeSpecific, warranties } from 'src/types';
import { enUS } from 'date-fns/locale';
import { dateFnsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';

export const currentYear = new Date().getFullYear();
//this will give options from 2023 and so on
export const yearOptions = Array.from(
  new Array(currentYear - 2023 + 1),
  (val, index) => 2023 + index
);

export const isDateValid = (dateOrString: any) => {
  if (dateOrString) {
    return false;
  }
  // if (dateOrString instanceof Date) {
  //   return true;
  // }
  return isValid(dateOrString);
};

export const dateToday = (formatStr: string = 'PPpp') => {
  const today = format(new Date(), formatStr);
  return today;
};

export const dateTodayVanilla = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const dateEndTodayVanilla = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set time to 11:59 PM
  return today;
};

export const isDateMidnight = (dateOrStrParam: Date | string) => {
  if (dateOrStrParam instanceof Date) {
    const time = dateOrStrParam.toISOString().split('T')[1];
    if (time === '00:00:00.000Z') {
      return true;
    }
  }
  if (
    String(dateOrStrParam).match(
      /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/
    ) !== null
  ) {
    return true;
  }

  const dateTimeISO = new Date(dateOrStrParam).toISOString().split('T')[1];
  if (dateTimeISO === '00:00:00.000Z') {
    return true;
  }

  return false;
};

export const formatDate = (
  dateOrStr?: Date | string | null,
  pattern = 'PP'
) => {
  if (!dateOrStr) {
    return 'Invalid Date';
  }
  if (dateOrStr === '0000-00-00 00:00:00') {
    return 'Invalid Date';
  }
  if (isDateMidnight(dateOrStr)) {
    const utcLocal = convertUTCtoLocal(dateOrStr);
    return `${format(utcLocal, pattern)}`;
  }
  return format(toDate(dateOrStr), pattern);
};

// wrong spelling
export const formatingDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date
    .toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })
    .replace(' at', '');
};

export const toUtcEquivalentDate = (date: Date | null) => {
  if (!date) {
    return undefined;
  }
  return new Date(format(date, "yyyy-MM-dd'T'00:00:00.000") + 'Z');
};

export const getWarrantyInDays = (val: string) => {
  if (val) {
    const daysValue = warranties.find((x) => x.name === val)?.value;
    // accept zero days as legit value
    if (daysValue !== undefined && daysValue >= 0) {
      return daysValue;
    }
    if (!isNaN(+val) && +val > 0) {
      return Math.round(+val);
    }
  }
  return undefined;
};

export const toDate = (date: Date | string): Date => {
  if (date instanceof Date) {
    return date;
  } else {
    return new Date(date);
  }
};

export const convertUTCtoLocal = (dateOrString: Date | string) => {
  const date = toDate(dateOrString);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const muiDatePickerToDateStr = (date: MaterialUiPickersDate) => {
  return toUtcEquivalentDate(date)?.toISOString();
};

export const timeStampNow = () => +new Date();

export const transformerDateField = (date: MaterialUiPickersDate) => {
  const dateUtc = formatDate(date || '', 'yyyy-MM-dd');
  return dateUtc;
};

const locales = {
  'en-US': enUS
};

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

export const monthNames = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec'
];

export const convertToLocalISO = (
  selectedDate: Date | string | null,
  endOfDay = false
): string | null => {
  if (!selectedDate) return null; // Handle null case

  let date =
    selectedDate instanceof Date ? selectedDate : new Date(selectedDate);

  if (endOfDay) {
    date.setHours(23, 59, 59, 999); // Set to end of day
  } else {
    date.setHours(0, 0, 0, 0); // Explicitly set to 12:00 AM
  }

  let tzOffsetMinutes = -date.getTimezoneOffset(); // Get correct timezone offset in minutes
  let tzOffsetHours = Math.floor(Math.abs(tzOffsetMinutes) / 60);
  let tzOffsetMins = Math.abs(tzOffsetMinutes) % 60;
  let tzSign = tzOffsetMinutes >= 0 ? '+' : '-';

  let tzFormatted = `${tzSign}${String(tzOffsetHours).padStart(
    2,
    '0'
  )}:${String(tzOffsetMins).padStart(2, '0')}`;

  // Convert manually to ISO format with local timezone
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}T${String(
    date.getHours()
  ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(
    date.getSeconds()
  ).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(
    3,
    '0'
  )}${tzFormatted}`;
};

const formatLocalDate = (date: Date) => {
  return date.toLocaleDateString('en-CA', {
    // en-CA gives YYYY-MM-DD
    timeZone: 'Asia/Manila'
  });
};

export const cleanDate = (date: string) =>
  date ? dayjs(date).format('YYYY-MM-DD') : undefined;

//this get preset date value base on date range like today | week | month and yesterday
export const getDateRangePreset = (
  preset: DateRangeSpecific,
  isLastDate = false
) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const shiftToLastMonth = (date: Date) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() - 1);
    return d;
  };

  if (preset === 'today') {
    const start = isLastDate ? shiftToLastMonth(today) : today;
    const end = new Date(start);
    return { start: formatLocalDate(start), end: formatLocalDate(end) };
  }

  if (preset === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const start = isLastDate ? shiftToLastMonth(yesterday) : yesterday;
    const end = new Date(start);
    return { start: formatLocalDate(start), end: formatLocalDate(end) };
  }

  if (preset === 'week') {
    const dayOfWeek = today.getDay(); // 0=Sun ... 6=Sat
    const saturday = new Date(today);
    const diffToSaturday = (dayOfWeek + 1) % 7;
    saturday.setDate(today.getDate() - diffToSaturday);

    const friday = new Date(saturday);
    friday.setDate(saturday.getDate() + 6);

    const start = isLastDate ? shiftToLastMonth(saturday) : saturday;
    const end = isLastDate ? shiftToLastMonth(friday) : friday;
    return { start: formatLocalDate(start), end: formatLocalDate(end) };
  }

  if (preset === 'month') {
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today);

    if (isLastDate) {
      // last month first day
      const prevFirst = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prevEnd = new Date(
        prevFirst.getFullYear(),
        prevFirst.getMonth(),
        lastDay.getDate()
      );
      return {
        start: formatLocalDate(prevFirst),
        end: formatLocalDate(prevEnd)
      };
    }

    return { start: formatLocalDate(firstDay), end: formatLocalDate(lastDay) };
  }

  return null;
};

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getPresetDateRange = (preset?: string | DateRangeSpecific) => {
  if (!preset) {
    console.error(`Unknown date range: ${preset}`);
    return {};
  }
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  switch (preset?.toLowerCase()) {
    case 'today':
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'yesterday':
      startDate.setDate(today.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate.setDate(today.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'week':
      startDate.setDate(today.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'month':
      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(1);

      endDate.setMonth(today.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    default:
      console.error(`Unknown date range: ${preset}`);
      return {};
  }

  return {
    startDate: formatDateLocal(startDate),
    endDate: formatDateLocal(endDate)
  };
};

export const muiDateToYMDHMS = (date: MaterialUiPickersDate): string | null => {
  if (!date) return null; // handle null/undefined
  const parsedDate = new Date(date); // works if it's already a Date or string
  return format(parsedDate, 'yyyy-MM-dd HH:mm:ss');
};

export const muiDateTimeToHHmm = (
  dateTime: MaterialUiPickersDate
): string | undefined => {
  if (!dateTime) return undefined;
  const parsedDate = new Date(dateTime);
  return format(parsedDate, 'HH:mm');
};

export const timeStringToDate = (time?: string) => {
  if (!time) return null;
  const [h, m, s] = time.split(':').map(Number);
  const dateTime = new Date();
  dateTime.setHours(h, m, s ?? 0, 0);
  return dateTime;
};

export const getQuarterEnabledMonths = (
  from: Date | null,
  to: Date | null
): number[] => {
  if (!from || !to) return [];

  const fromMonth = from.getMonth(); // 0 = Jan
  const toMonth = to.getMonth();

  const months: number[] = [];

  for (let m = fromMonth; m <= toMonth; m++) {
    const monthInQuarter = (m % 3) + 1; // 1 to 3 within quarter
    months.push(monthInQuarter);
  }

  return months;
};

export const normalizeToSqlDatetime = (dateStr: string | null) => {
  if (!dateStr) return null;

  return moment(dateStr, [
    'YYYY-MM-DD HH:mm:ss',
    'MMMM D, YYYY hh:mm:ss A'
  ]).format('YYYY-MM-DD HH:mm:ss');
};

export const getTime = (value?: string) => {
  if (!value) return null;
  const t = Date.parse(value);
  return isNaN(t) ? null : t;
};

export const getDurationToNext = (current: string, next?: string | null) => {
  if (!next) return '-';

  const currentDate = parseISO(current);
  const nextDate = parseISO(next);

  let seconds = Math.abs(differenceInSeconds(nextDate, currentDate));

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;

  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;

  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;

  return `${seconds}s`;
};

export const getTotalTimelineDuration = (
  timeline: { created_at: string }[]
) => {
  if (!timeline?.length || timeline.length < 2) return '-';

  return getDurationToNext(
    timeline[0].created_at,
    timeline[timeline.length - 1].created_at
  );
};

export const getRunningDuration = (createdAt: string) => {
  if (!createdAt) return '-';

  const start = parseISO(createdAt);
  const now = new Date();

  const seconds = differenceInSeconds(now, start);

  const format = (secs: number) => {
    const days = Math.floor(secs / 86400);
    const hours = Math.floor((secs % 86400) / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const sec = secs % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${sec}s`;
    return `${sec}s`;
  };

  return format(seconds);
};
